<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { isTagOnly } from '@/todos/hashtags'
import ChipEditor from './ChipEditor.vue'

const MAX_LENGTH = 500

const props = defineProps<{
  placeholder?: string
  disabled?: boolean
  autofocus?: boolean
  prefillTag?: string | null // (on a tag page: new todos start with this tag as a chip)
}>()
const { t } = useI18n()
const emit = defineEmits<{ add: [summary: string] }>()

// The one line add field: a `ChipEditor` (text and tag chips) and the Add button; Enter in the editor adds as well.
const editor = ref<InstanceType<typeof ChipEditor> | null>(null)
const plain = ref('')

// Nothing to add while the field only holds the prefilled tag.
const nothingToAdd = computed(
  () => !plain.value.trim() || (!!props.prefillTag && isTagOnly(plain.value, props.prefillTag)),
)

function submit(): void {
  const summary = plain.value.trim()
  if (!summary || nothingToAdd.value || props.disabled) return
  emit('add', summary)
  editor.value?.clear()
}

defineExpose({ focus: () => editor.value?.focus() })
</script>

<template>
  <form class="quick-add" @submit.prevent="submit">
    <ChipEditor
      ref="editor"
      v-model:plain="plain"
      class="editor"
      :placeholder="placeholder"
      :disabled="disabled"
      :autofocus="autofocus"
      :prefill-tag="prefillTag"
      :max-length="MAX_LENGTH"
      @submit="submit"
    />
    <button type="submit" class="ui-btn ui-btn-primary" :disabled="disabled || nothingToAdd">
      {{ t('todos.add') }}
    </button>
  </form>
</template>

<style scoped>
.quick-add {
  display: flex;
  gap: 0.5rem;
}

.editor {
  flex: 1;
}
</style>
